import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { SyntheticEvent } from 'react';
import { Icon } from './Icon';
import { CATEGORY_ICON } from '@/data/categories';
import { MIN_USABLE_LOGO_PX, faviconUrl } from '@/lib/logos';
import { scriptAttributes } from '@/lib/text';
import type { Tool } from '@/types';
import styles from './ToolCard.module.css';

export interface ToolCardProps {
  readonly tool: Tool;
  /** Position in the grid, used to stagger the entrance animation. */
  readonly index?: number | undefined;
  /** Play the entrance animation. Off once the view has settled. */
  readonly animate?: boolean | undefined;
}

export function ToolCard({ tool, index = 0, animate = false }: ToolCardProps) {
  const [logoFailed, setLogoFailed] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Most tool names are Latin and need LTR plus an English `lang` so screen
  // readers switch voice; the Arabic ones must inherit the page instead.
  const script = scriptAttributes(tool.name);

  /**
   * Google's favicon service answers unknown domains with a 16×16 grey globe
   * rather than a 404, so a successful load still has to be measured before it
   * is trusted. Anything smaller falls back to the tool's monogram.
   */
  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    if (event.currentTarget.naturalWidth < MIN_USABLE_LOGO_PX) {
      setLogoFailed(true);
      return;
    }
    setLogoLoaded(true);
  };

  return (
    <article
      className={`${styles.card} ${animate ? styles.enter : ''}`}
      // Cap the cascade: past the first row or two the delay stops growing, so
      // the last of 27 cards is not left waiting on the 26 before it.
      style={animate ? ({ '--stagger': Math.min(index, 7) } as CSSProperties) : undefined}
    >
      <div className={styles.top}>
        <span className={styles.logo}>
          {/* The monogram is always painted; the favicon layers over it once it
              arrives. Swapping only on failure left an empty box for the whole
              duration of a slow request. */}
          <span className={styles.monogram} aria-hidden="true" {...script}>
            {tool.monogram}
          </span>

          {logoFailed ? null : (
            <img
              className={`${styles.logoImage} ${logoLoaded ? styles.logoImageReady : ''}`}
              src={faviconUrl(tool.domain)}
              alt=""
              width={28}
              height={28}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={() => setLogoFailed(true)}
              onLoad={onLoad}
            />
          )}
        </span>

        <span className={styles.category}>
          <Icon name={CATEGORY_ICON[tool.category]} size={16} className={styles.categoryIcon} />
          {tool.category}
        </span>
      </div>

      <h3 className={styles.name} {...script}>
        {tool.name}
      </h3>

      <p className={styles.description}>{tool.description}</p>

      <a className={styles.link} href={tool.url} target="_blank" rel="noopener noreferrer">
        الذهاب للأداة
        <span className="visually-hidden"> — {tool.name} (يفتح في نافذة جديدة)</span>
      </a>
    </article>
  );
}
